import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { CommonRootState } from '../combineReducers';
import { IServerTokens, traceHeaderName } from '../interfaces/serverTokens';
import {
  registerAcceptLanguageInterceptor,
  registerBearerTokenInterceptor,
  registerTraceHeaderInterceptor
} from '../helpers/interceptors';
import { ThunkAction } from 'redux-thunk';
import i18next from 'i18next';
import { enUS } from '@pec/aion-ui-i18next/constants';

const fetchServerTokensRequest = () =>
  ({
    type: 'FETCH_SERVER_TOKENS_REQUEST'
  } as const);

const fetchServerTokensSuccess = (payload: IServerTokens) =>
  ({
    type: 'FETCH_SERVER_TOKENS_SUCCESS',
    payload
  } as const);

const fetchServerTokensFailure = (error: AxiosError) => {
  console.error(error);
  return {
    type: 'FETCH_SERVER_TOKENS_FAILURE',
    error
  } as const;
};

const logOut = () =>
  ({
    type: 'LOGOUT'
  } as const);

const gmapsLoaded = () =>
  ({
    type: 'GMAPS_LOADED'
  } as const);

const pendoLoaded = () =>
  ({
    type: 'PENDO_LOADED'
  } as const);

export const handleLogOut = (
  logoutUrl = '/spapi/auth/logout'
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  dispatch(logOut());
  Cookies.remove('AionCurrentOrganizationId');
  window.location.assign(logoutUrl);
};

declare global {
  interface Window {
    _AION_gmapsLoaded: () => void;
  }
}

export const registerServerTokens = (
  serverTokens: IServerTokens
): ThunkAction<void, CommonRootState, null, Actions> => dispatch => {
  window._AION_gmapsLoaded = () => {
    dispatch(gmapsLoaded());
  };
  appendGoogleMapsScript(serverTokens.googleMapsApiKey);
  dispatch(appendPendoScript(serverTokens.pendoApiKey));
  registerBearerTokenInterceptor(serverTokens.accessToken, error => dispatch(fetchServerTokensFailure(error)));
  registerTraceHeaderInterceptor(serverTokens[traceHeaderName]);
  registerAcceptLanguageInterceptor(); // TODO: Discuss whether this should be moved to RegisterInterceptors.tsx (aion-ui-components)
  dispatch(fetchServerTokensSuccess(serverTokens));
};

export const LOCALE_TO_GOOGLE_MAPS: Record<string, { language: string; region: string }> = {
  'en-AU': { language: 'en', region: 'AU' },
  'en-CA': { language: 'en', region: 'CA' },
  'en-GB': { language: 'en-GB', region: 'GB' },
  'en-US': { language: 'en', region: 'US' },
  'de-AT': { language: 'de', region: 'AT' },
  'de-DE': { language: 'de', region: 'DE' },
  'es-AR': { language: 'es', region: 'AR' },
  'es-ES': { language: 'es', region: 'ES' },
  'es-MX': { language: 'es-419', region: 'MX' },
  'fr-BE': { language: 'fr', region: 'BE' },
  'fr-CA': { language: 'fr-CA', region: 'CA' },
  'fr-CH': { language: 'fr', region: 'CH' },
  'fr-FR': { language: 'fr', region: 'FR' },
  'it-CH': { language: 'it', region: 'CH' },
  'it-IT': { language: 'it', region: 'IT' },
  'nl-BE': { language: 'nl', region: 'BE' },
  'nl-NL': { language: 'nl', region: 'NL' },
  'pt-BR': { language: 'pt-BR', region: 'BR' },
  'pt-PT': { language: 'pt-PT', region: 'PT' },
  'ro-RO': { language: 'ro', region: 'RO' },
};

const getGoogleMapsLocalization = (): { language: string; region: string } => {
  const locale = i18next.language || enUS;

  return LOCALE_TO_GOOGLE_MAPS[locale] ?? LOCALE_TO_GOOGLE_MAPS[enUS];
};

const appendGoogleMapsScript = (googleMapsApiKey: string) => {
  const { language, region } = getGoogleMapsLocalization();
  const gmaps = document.createElement('script');
  gmaps.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&callback=_AION_gmapsLoaded&libraries=places&language=${language}&region=${region}`;
  gmaps.defer = true;
  gmaps.dataset.aionGmaps = "true";
  document.head.appendChild(gmaps);
};

export const reinitGoogleMapsScript = () => {
  const GMAPS_SCRIPT_SELECTOR = 'script[data-aion-gmaps]';
  const existing = document.querySelector<HTMLScriptElement>(GMAPS_SCRIPT_SELECTOR);
  const googleMapsApiKey = existing ? new URL(existing.src).searchParams.get('key') ?? '' : '';

  document.querySelectorAll<HTMLScriptElement>(GMAPS_SCRIPT_SELECTOR).forEach(s => s.parentNode?.removeChild(s));

  if ((window as any).google) {
    delete (window as any).google;
  }
  appendGoogleMapsScript(googleMapsApiKey);
};

const appendPendoScript = (
  pendoApiKey: string
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  const pendo = document.createElement('script');
  pendo.src = `https://cdn.pendo.io/agent/static/${pendoApiKey}/pendo.js`;
  pendo.defer = true;
  document.head.appendChild(pendo);

  pendo.addEventListener(
    'load',
    () => {
      dispatch(pendoLoaded());
    },
    { once: true }
  );
};

const fetchServerTokens = (): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchServerTokensRequest());

    const {
      data: { serverTokens, redirectUri }
    } = await axios.get<{ serverTokens?: IServerTokens; redirectUri?: string }>('/spapi/serverTokens');

    if (serverTokens) {
      dispatch(registerServerTokens(serverTokens));
    } else if (redirectUri) {
      window.location.assign(redirectUri);
    }
  } catch (error) {
    dispatch(fetchServerTokensFailure(error));
  }
};

const shouldFetchServerTokens = ({ serverTokens: { isFetching, serverTokens } }: CommonRootState) =>
  !isFetching && !serverTokens;

export const fetchServerTokensIfNeeded = (): ThunkAction<void, CommonRootState, null, Actions> => (
  dispatch,
  getState
) => {
  if (shouldFetchServerTokens(getState())) {
    dispatch(fetchServerTokens());
  }
};

export type Actions =
  | ReturnType<typeof fetchServerTokensRequest>
  | ReturnType<typeof fetchServerTokensSuccess>
  | ReturnType<typeof fetchServerTokensFailure>
  | ReturnType<typeof gmapsLoaded>
  | ReturnType<typeof logOut>
  | ReturnType<typeof pendoLoaded>;
